<template>
    <MuvalModal
        v-model="user.tenantModalStatus"
        modalSize="md:w-[448px]"
        close-button
    >
        <template #header>
            <div
                v-if="leavingTenant"
                class="flex items-center gap-3 text-muval-gray-1"
            >
                <button
                    class="flex items-center justify-center"
                    @click="cancelLeave"
                >
                    <SvgIcon
                        name="system-back"
                        class="size-6"
                    />
                </button>
                <span class="text-lg font-medium">Leave organisation?</span>
            </div>
            <div
                v-else
                class="flex text-lg font-medium text-muval-gray-1"
            >
                Select organisation
            </div>
        </template>

        <!-- Leave confirmation view -->
        <div v-if="leavingTenant">
            <p class="text-base text-muval-gray-1">
                Are you sure you want to leave <span class="font-medium">{{ leavingTenant.name }}</span
                >?
            </p>
            <div class="mt-6 flex items-center justify-end gap-3">
                <MuvalButton
                    quaternary
                    @click="cancelLeave"
                >
                    No, cancel
                </MuvalButton>
                <MuvalButton
                    :loading="leavingTenantId === leavingTenant.id"
                    @click="confirmLeave"
                >
                    Yes, I'm sure
                </MuvalButton>
            </div>
        </div>

        <!-- Tenant list view -->
        <div
            v-else
            class="max-h-[70vh] overflow-y-auto overscroll-y-contain"
        >
            <TenantList
                :tenants="tenants"
                :pending-invites="pendingInvites"
                :current-tenant-id="user.tenantId"
                :switching-to-tenant-id="switchingToTenantId"
                :joining-tenant-ids="joiningTenantIds"
                :accepting-id="acceptingId"
                :declining-id="decliningId"
                :leaving-tenant-id="leavingTenantId"
                @select="(t) => selectTenant(t, { fetchNotifications: false, closeModal: tenantModal.close })"
                @accept="acceptInvite"
                @decline="declineInvite"
                @leave="promptLeave"
            />
        </div>
    </MuvalModal>
</template>

<script setup>
import MuvalModal from '@components/modal/MuvalModal.vue';
import TenantList from '@components/TenantList.vue';
import { useTenantSelection } from '@composables/useTenantSelection';

const user = useUserStore();

const keys = useMagicKeys({
    passive: false,
    onEventFired(e) {
        if (e.key === 'e' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
        }
    },
});

const keyCtrlE = keys['Ctrl+E'];
const keyCmdE = keys['Cmd+E'];
const keyEscape = keys['escape'];

const tenantModal = {
    open: () => {
        user.showTenantModal();
    },
    close: () => {
        user.hideTenantModal();
        leavingTenant.value = null;
    },
    toggle: () => {
        user.toggleTenantModal();
    },
};

const {
    tenants,
    pendingInvites,
    switchingToTenantId,
    joiningTenantIds,
    acceptingId,
    decliningId,
    leavingTenantId,
    fetchPendingInvites,
    selectTenant,
    acceptInvite,
    declineInvite,
    leaveTenant,
} = useTenantSelection();

const leavingTenant = ref(null);

function promptLeave(tenant) {
    leavingTenant.value = tenant;
}

function cancelLeave() {
    leavingTenant.value = null;
}

function confirmLeave() {
    if (leavingTenant.value) {
        leaveTenant(leavingTenant.value);
        leavingTenant.value = null;
    }
}

watch(
    () => user.tenantModalStatus,
    async (isOpen) => {
        if (isOpen) {
            await fetchPendingInvites();
        } else {
            leavingTenant.value = null;
        }
    },
);

whenever(keyCtrlE, tenantModal.toggle);
whenever(keyCmdE, tenantModal.toggle);
whenever(keyEscape, () => {
    if (leavingTenant.value) {
        cancelLeave();
    } else {
        tenantModal.close();
    }
});
</script>
